import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Check, Crown, Sparkles, ArrowRight, Loader2, CheckCircle, XCircle, Users, Baby, CreditCard, AlertTriangle, ExternalLink, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PlanCard } from '@/components/PlanCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Billing = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [stripeProducts, setStripeProducts] = useState(null);
  const [contractStatus, setContractStatus] = useState(null);
  const [subDetails, setSubDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const successHandled = useRef(false);

  const isParent = user?.role === 'parent';
  const isNanny = user?.role === 'nanny';

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user?.id]);

  // Handle Stripe redirect ONLY when auth is ready
  useEffect(() => {
    if (user && !successHandled.current) {
      handleSuccessRedirect();
    }
  }, [user?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pricingRes, statusRes, subRes, stripeProductsRes] = await Promise.all([
        axios.get(`${API}/payments/pricing`),
        axios.get(`${API}/payments/user/contract-status`),
        axios.get(`${API}/payments/subscription-details`).catch(() => ({ data: { has_subscription: false } })),
        axios.get(`${API}/payments/stripe-products`).catch(() => null),
      ]);
      setPricing(pricingRes.data);
      setContractStatus(statusRes.data);
      setSubDetails(subRes.data);
      
      // Merge Stripe products with local pricing config
      if (stripeProductsRes?.data) {
        setStripeProducts(stripeProductsRes.data);
      }
      
      // Re-fetch contract status after subscription sync (subscription-details syncs with Stripe)
      if (subRes.data?.has_subscription) {
        const refreshedStatus = await axios.get(`${API}/payments/user/contract-status`);
        setContractStatus(refreshedStatus.data);
      }
    } catch (err) {
      console.error('Error fetching billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessRedirect = async () => {
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');
    if (canceled) {
      toast.info('Paiement annulé');
      setSearchParams({});
      return;
    }
    if (sessionId && !successHandled.current) {
      successHandled.current = true;
      try {
        await axios.post(`${API}/payments/verify-checkout`, { session_id: sessionId });
        toast.success('Abonnement activé avec succès !');
        if (refreshUser) refreshUser();
        await fetchData();
        // Notifier Navigation que le plan a changé
        window.dispatchEvent(new Event('planUpdated'));
      } catch (err) {
        toast.error("Erreur lors de la vérification du paiement");
      }
      setSearchParams({});
    }
  };

  const handleCheckout = async (planType) => {
    try {
      setCheckoutLoading(true);
      const payload = {
        plan_type: planType,
        billing_period: billingPeriod,
      };
      
      const res = await axios.post(`${API}/payments/create-checkout`, payload);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du paiement");
      setCheckoutLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setCancelLoading(true);
      const res = await axios.post(`${API}/payments/cancel-subscription`);
      toast.success(res.data.message);
      setShowCancelDialog(false);
      if (refreshUser) refreshUser();
      await fetchData();
      // Notifier Navigation que le plan a changé
      window.dispatchEvent(new Event('planUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la résiliation");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setReactivateLoading(true);
      const res = await axios.post(`${API}/payments/reactivate-subscription`);
      toast.success(res.data.message);
      if (refreshUser) refreshUser();
      await fetchData();
      // Notifier Navigation que le plan a changé
      window.dispatchEvent(new Event('planUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la réactivation");
    } finally {
      setReactivateLoading(false);
    }
  };

  const handlePortal = async () => {
    try {
      setPortalLoading(true);
      const res = await axios.post(`${API}/payments/create-portal-session`);
      window.open(res.data.portal_url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'accéder au portail Stripe");
    } finally {
      setPortalLoading(false);
    }
  };

  const formatPrice = (priceObj) => {
    if (!priceObj) return '…';
    return `${priceObj.price_float.toFixed(2).replace('.', ',')}${priceObj.currency_symbol}`;
  };
  
  const getDisplayPrice = (planKey, period) => {
    // If Stripe products are available and loaded, use them
    if (stripeProducts && stripeProducts[planKey]) {
      const stripeProduct = stripeProducts[planKey];
      const stripePrice = stripeProduct.prices?.[period];
      if (stripePrice) {
        return {
          price_float: stripePrice.amount / 100,
          price_display: stripePrice.amount_display,
          currency_symbol: '€',
          price_cents: stripePrice.amount,
          interval: stripePrice.interval,
          interval_label: period === 'monthly' ? 'mois' : 'an',
          stripe_price_id: stripePrice.id,
        };
      }
    }
    // Fallback to hardcoded pricing
    if (pricing && pricing[planKey] && pricing[planKey][period]) {
      return pricing[planKey][period];
    }
    return null;
  };
  
  const getDisplayPlanName = (planKey) => {
    if (stripeProducts && stripeProducts[planKey]) {
      return stripeProducts[planKey].name;
    }
    if (pricing && pricing[planKey]) {
      return pricing[planKey].plan_name;
    }
    return planKey === 'parent_pro' ? 'Parent Pro' : 'Nounou Pro';
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '—';
    try {
      return new Date(isoStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return isoStr; }
  };

  const isActive = contractStatus?.effective_premium;
  const hasOwnSub = contractStatus?.is_premium;
  const hasPartnerSub = contractStatus?.has_premium_relation;
  const myPlan = isParent ? 'parent_pro' : 'nanny_pro';

  // Subscription management flags
  const subIsActive = subDetails?.has_subscription && ['active', 'trialing'].includes(subDetails?.status);
  const subIsCanceling = subDetails?.cancel_at_period_end;
  const subIsCanceled = subDetails?.status === 'canceled';

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-80" /><Skeleton className="h-80" /><Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-8" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>Abonnement</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
          La gestion de garde d'enfants, simplifiée — un seul abonnement, zéro prise de tête.
        </p>
      </div>

      {/* Current Status */}
      {isActive && (
        <Alert className="border-[var(--success)] bg-[var(--success-light)]" data-testid="active-plan-alert">
          <CheckCircle className="h-4 w-4 text-[var(--success)]" />
          <AlertDescription>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div>
                <p className="font-semibold text-sm text-[var(--success)]">
                  {hasOwnSub
                    ? `Vous êtes abonné ${contractStatus.current_plan === 'parent_pro' ? 'Parent Pro' : contractStatus.current_plan === 'nanny_pro' ? 'Nounou Pro' : 'Premium'}`
                    : "Votre collaboration est en version complète"}
                </p>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                  {hasPartnerSub && !hasOwnSub
                    ? "Un abonnement partagé par votre partenaire active les fonctionnalités complètes."
                    : contractStatus?.message}
                </p>
              </div>
              {hasOwnSub && subDetails?.current_period_end && (
                <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5 shrink-0">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>
                    {subIsCanceling 
                      ? `Accès jusqu'au ${formatDate(subDetails.current_period_end)}`
                      : `Prochain prélèvement le ${formatDate(subDetails.current_period_end)}`}
                  </span>
                </div>
              )}
            </div>
            {subIsCanceling && (
              <p className="text-xs text-amber-600 mt-2 bg-amber-50 dark:bg-amber-950/20 p-2 rounded">
                Résiliation programmée — vous garderez l'accès complet jusqu'au {formatDate(subDetails.current_period_end)}.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}


      {/* Shared premium explanation */}
      {!isActive && (
        <div className="p-4 rounded-lg border border-[var(--primary-200)] bg-[var(--primary-50)]" data-testid="shared-premium-banner">
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-[var(--primary-500)] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[var(--primary-700)]">Un seul abonnement suffit</p>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Si vous ou votre {isParent ? 'nounou' : 'employeur'} avez un abonnement, vous profitez tous les deux des fonctionnalités complètes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3">
        <Label htmlFor="billing-toggle" className={cn("text-sm", billingPeriod === 'monthly' ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}>
          Mensuel
        </Label>
        <Switch
          id="billing-toggle"
          checked={billingPeriod === 'annual'}
          onCheckedChange={(v) => setBillingPeriod(v ? 'annual' : 'monthly')}
          data-testid="billing-period-toggle"
        />
        <Label htmlFor="billing-toggle" className={cn("text-sm", billingPeriod === 'annual' ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}>
          Annuel
        </Label>
        {billingPeriod === 'annual' && (
          <Badge variant="secondary" className="text-[10px] bg-[var(--success-light)] text-[var(--success)] border-0">
            -17%
          </Badge>
        )}
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free Plan */}
        <Card className="border-2 border-[var(--neutral-200)]" data-testid="plan-free">
          <CardHeader className="text-center pb-3 pt-6">
            <CardTitle className="text-xl" style={{ color: 'var(--text-primary)' }}>Gratuit</CardTitle>
            <CardDescription>{pricing?.free?.tagline}</CardDescription>
            <div className="mt-4">
              <span className="text-4xl font-bold" style={{ color: 'var(--text-primary)' }}>0€</span>
              <span className="text-sm text-[var(--text-muted)]">/{billingPeriod === 'monthly' ? 'mois' : 'an'}</span>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2.5">
              {(pricing?.free?.features || []).map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                  <Check className="h-4 w-4 mt-0.5 text-[var(--text-muted)] shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {!hasOwnSub && !isActive && (
              <div className="mt-6 text-center">
                <Badge variant="outline" className="text-xs text-[var(--text-muted)]">Plan actuel</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Parent Pro */}
        <PlanCard
          plan={{
            ...pricing?.parent_pro,
            plan_name: getDisplayPlanName('parent_pro'),
            monthly: getDisplayPrice('parent_pro', 'monthly'),
            annual: getDisplayPrice('parent_pro', 'annual'),
          }}
          planKey="parent_pro"
          billingPeriod={billingPeriod}
          formatPrice={formatPrice}
          isMyPlan={isParent}
          isActive={hasOwnSub && contractStatus?.current_plan === 'parent_pro'}
          isOtherActive={hasPartnerSub && !hasOwnSub && !isParent}
          canBuy={contractStatus?.can_buy && isParent}
          onCheckout={handleCheckout}
          checkoutLoading={checkoutLoading}
          recommended={isParent}
          icon={<Baby className="h-5 w-5" />}
          accentColor="var(--secondary-400)"
          subDetails={hasOwnSub && contractStatus?.current_plan === 'parent_pro' ? subDetails : null}
          onCancel={() => setShowCancelDialog(true)}
          onReactivate={handleReactivate}
          reactivateLoading={reactivateLoading}
          onPortal={handlePortal}
          portalLoading={portalLoading}
        />

        {/* Nounou Pro */}
        <PlanCard
          plan={{
            ...pricing?.nanny_pro,
            plan_name: getDisplayPlanName('nanny_pro'),
            monthly: getDisplayPrice('nanny_pro', 'monthly'),
            annual: getDisplayPrice('nanny_pro', 'annual'),
          }}
          planKey="nanny_pro"
          billingPeriod={billingPeriod}
          formatPrice={formatPrice}
          isMyPlan={isNanny}
          isActive={hasOwnSub && contractStatus?.current_plan === 'nanny_pro'}
          isOtherActive={hasPartnerSub && !hasOwnSub && !isNanny}
          canBuy={contractStatus?.can_buy && isNanny}
          onCheckout={handleCheckout}
          checkoutLoading={checkoutLoading}
          recommended={isNanny}
          icon={<Crown className="h-5 w-5" />}
          accentColor="var(--primary-500)"
          subDetails={hasOwnSub && contractStatus?.current_plan === 'nanny_pro' ? subDetails : null}
          onCancel={() => setShowCancelDialog(true)}
          onReactivate={handleReactivate}
          reactivateLoading={reactivateLoading}
          onPortal={handlePortal}
          portalLoading={portalLoading}
        />
      </div>

      {/* Bottom note */}
      <div className="text-center text-xs text-[var(--text-muted)] space-y-1 pb-4">
        <p>Paiement sécurisé par Stripe • Annulation en 1 clic • Sans engagement</p>
        {pricing?.shared_message && <p className="font-medium">{pricing.shared_message}</p>}
        {stripeProducts && stripeProducts.source === 'stripe' && (
          <p className="text-[10px] text-[var(--success)] flex items-center justify-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Prix chargés depuis votre configuration Stripe
          </p>
        )}
        {stripeProducts && stripeProducts.source === 'hardcoded' && (
          <p className="text-[10px] text-[var(--text-tertiary)] flex items-center justify-center gap-1">
            Prix de configuration par défaut (Stripe non disponible)
          </p>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-secondary)' }}>Résilier votre abonnement ?</DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <p>Votre abonnement restera actif jusqu'à la fin de la période en cours{subDetails?.current_period_end ? ` (${formatDate(subDetails.current_period_end)})` : ''}.</p>
              <p>Après cette date, vous passerez au plan gratuit (1 enfant max, historique limité).</p>
              <p className="font-medium text-[var(--text-primary)]">Vous pourrez réactiver à tout moment.</p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowCancelDialog(false)} data-testid="cancel-dialog-keep">
              Garder mon abonnement
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelLoading}
              data-testid="cancel-dialog-confirm"
            >
              {cancelLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <XCircle className="h-4 w-4 mr-1.5" />}
              Confirmer la résiliation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Billing;
